'use client';

import { isPersistedFeedbackMessage } from '@/features/feedback/lib/ai-feedback';
import clsx from 'clsx';
import { Pin, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { ChatMessage, TaskFile } from '@/entities/interview-task';
import { chatStyles } from '@/shared/ui/chat/styles';
import { ChatMarkdown } from '@/shared/ui/markdown';

import type { AttachStatus } from '../lib/status-palette';
import { FileCard } from './file-card';
import { QuizCard } from './quiz-card';

type MessageProps = {
	contentClassName?: string;
	m: ChatMessage;
	onOpenFile?: (file: TaskFile) => void;
	onOpenQuiz?: () => void;
	/** Кнопки пошаговой формы под текстом того же сообщения. */
	children?: React.ReactNode;
	getAttachStatus?: (file: TaskFile) => AttachStatus | null;
	quizStatus?: 'pending' | 'accepted' | null;
	onRate?: (messageId: number, sentiment: 'up' | 'down') => void;
	highlightFiles?: boolean;
	/** Запустить typewriter-эффект для текста (только для новых AI сообщений). */
	shouldAnimate?: boolean;
	/** Скролл-контейнер чата — подкручиваем его к низу пока идёт reveal. */
	scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
};

const MAX_W_WRAP = chatStyles.messageWidth;

// Скорость reveal: ~4 символа за тик ≈ 25 ms → 160 chars/sec.
const TYPEWRITER_CHUNK = 4;
const TYPEWRITER_TICK_MS = 25;

export function Message({
	contentClassName = MAX_W_WRAP,
	m,
	onOpenFile,
	onOpenQuiz,
	getAttachStatus,
	quizStatus,
	onRate,
	highlightFiles,
	shouldAnimate,
	scrollContainerRef,
	children,
}: MessageProps) {
	const attachList = m.attaches || [];

	if (m.role === 'system') {
		return (
			<div className={contentClassName}>
				<div className="my-3 flex justify-center">
					<div className="flex items-center gap-1.5 rounded-full border border-gray-4 bg-[#EBEDF0] px-3 py-1 text-sm font-medium text-gray-3">
						<Pin size={11} strokeWidth={2.2} />
						<span>{m.text}</span>
					</div>
				</div>
			</div>
		);
	}

	if (m.role === 'you') {
		return (
			<div className={contentClassName}>
				<div className="mb-7 flex justify-end">
					<div className="flex max-w-[78%] flex-col items-end">
						{m.text && <div className={chatStyles.userBubble}>{m.text}</div>}
						{attachList.length > 0 && (
							<div
								className={`${m.text ? 'mt-2' : ''} flex flex-col items-end gap-2`}
							>
								{attachList.map((file, idx) => (
									<FileCard
										key={idx}
										file={file}
										onOpen={(file) => onOpenFile?.(file)}
										status={getAttachStatus?.(file) ?? null}
										compact
									/>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	// Typewriter: на маунте фиксируем «нужна ли анимация» и стартуем интервал
	// reveal'а. Реальный текст уже целиком в m.text — постепенно показываем
	// его префикс. Когда reveal завершён, отдельным флагом разрешаем рендерить
	// non-text-части (quiz/attachments/feedback), чтобы они «всплыли» только
	// после текста.
	const willAnimateRef = useRef(!!shouldAnimate && !!m.text);
	const fullText = m.text ?? '';
	const [revealedLen, setRevealedLen] = useState(
		willAnimateRef.current ? 0 : fullText.length,
	);

	useEffect(() => {
		if (!willAnimateRef.current) return;
		const id = setInterval(() => {
			setRevealedLen((prev) => {
				const next = prev + TYPEWRITER_CHUNK;
				if (next >= fullText.length) {
					clearInterval(id);
					return fullText.length;
				}
				return next;
			});
		}, TYPEWRITER_TICK_MS);
		return () => clearInterval(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fullText]);

	const isRevealing = willAnimateRef.current && revealedLen < fullText.length;

	// Пока идёт reveal — прижимаем чат к низу, чтобы растущий текст не уезжал
	// за пределы видимой области. Используем тот же контейнер, на который
	// скроллит ChatPane при появлении новых сообщений.
	useLayoutEffect(() => {
		if (!isRevealing) return;
		const el = scrollContainerRef?.current;
		if (!el) return;
		el.scrollTop = el.scrollHeight;
	}, [revealedLen, isRevealing, scrollContainerRef]);

	return (
		<div className="group mb-8">
			{m.text && (
				<div className={contentClassName}>
					<div className={chatStyles.messageBody}>
						<ChatMarkdown>
							{willAnimateRef.current ? fullText.slice(0, revealedLen) : m.text}
						</ChatMarkdown>
					</div>
				</div>
			)}
			{m.quiz && !isRevealing && (
				<div className={clsx(contentClassName, m.text && 'mt-3.5')}>
					<QuizCard onOpen={() => onOpenQuiz?.()} status={quizStatus ?? null} />
				</div>
			)}
			{!isRevealing && attachList.length > 0 && (
				<div
					className={clsx(
						'w-full py-2 transition-colors duration-500',
						m.text || m.quiz ? 'mt-1.5' : '-mt-2',
						highlightFiles && 'bg-black/5 [animation:pulse_0.7s_ease-in-out_1]',
					)}
				>
					<div className={contentClassName}>
						<div className="flex flex-col gap-2">
							{attachList.map((file, idx) => (
								<FileCard
									key={idx}
									file={file}
									onOpen={(file) => onOpenFile?.(file)}
									status={getAttachStatus?.(file) ?? null}
								/>
							))}
						</div>
					</div>
				</div>
			)}
			{children && !isRevealing && (
				<div className={clsx(contentClassName, m.text && 'mt-3.5')}>
					{children}
				</div>
			)}
			{onRate && (
				<div className={clsx(contentClassName, isRevealing && 'invisible')}>
					<div className="mt-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
						<button
							disabled={!isPersistedFeedbackMessage(m.messageId)}
							type="button"
							onClick={() =>
								isPersistedFeedbackMessage(m.messageId) &&
								onRate(m.messageId, 'up')
							}
							title="Хороший ответ"
							className="flex size-8 items-center justify-center rounded-md text-gray-3 transition-colors hover:bg-gray-4 hover:text-gray-1"
						>
							<ThumbsUp size={16} strokeWidth={1.8} />
						</button>
						<button
							disabled={!isPersistedFeedbackMessage(m.messageId)}
							type="button"
							onClick={() =>
								isPersistedFeedbackMessage(m.messageId) &&
								onRate(m.messageId, 'down')
							}
							title="Пожаловаться на ответ"
							className="flex size-8 items-center justify-center rounded-md text-gray-3 transition-colors hover:bg-gray-4 hover:text-gray-1"
						>
							<ThumbsDown size={16} strokeWidth={1.8} />
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
